import React, { useState, useEffect, useCallback } from 'react';
import {
  Car, Truck, Wrench, Key, Plus, X, ChevronRight, AlertTriangle,
  CheckCircle, Clock, RefreshCw, User, Calendar, Gauge, DollarSign,
  ArrowLeft, Filter, Search, Edit2, Trash2, RotateCcw, Package,
  MapPin, Fuel, Settings, ChevronDown, ChevronUp
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// ─── helpers ──────────────────────────────────────────────────────────────────

const CATEGORIES = {
  fuel_truck:      { label: 'Fuel Truck',    icon: Truck,   color: '#f97316' },
  loaner:          { label: 'Loaner',         icon: Car,     color: '#3b82f6' },
  service_tarmac:  { label: 'Service/Tarmac', icon: Settings,color: '#8b5cf6' },
  staff_personal:  { label: 'Staff Personal', icon: User,    color: '#10b981' },
};

const STATUS_COLORS = {
  available:      { bg: '#052e16', text: '#4ade80', dot: '#22c55e' },
  in_use:         { bg: '#1e3a5f', text: '#60a5fa', dot: '#3b82f6' },
  maintenance:    { bg: '#451a03', text: '#fb923c', dot: '#f97316' },
  out_of_service: { bg: '#1f1f1f', text: '#9ca3af', dot: '#6b7280' },
};

const MAINT_TYPES = [
  'oil_change','tire_rotation','brake_service','inspection',
  'repair','body_work','electrical','fuel_system','scheduled','other'
];

const fmt = (v) => v ? new Intl.NumberFormat('en-US').format(v) : '—';
const fmtMoney = (v) => v != null ? `$${parseFloat(v).toFixed(2)}` : '—';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' }) : '—';

async function api(path, options = {}) {
  const token = localStorage.getItem('access_token');
  const r = await fetch(`${API_BASE}${path}`, {
    headers: { 
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers 
    },
    ...options,
    body: options.body ? JSON.stringify(options.body) : undefined,
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

// ─── Badge ─────────────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.out_of_service;
  return (
    <span style={{ background: c.bg, color: c.text }}
      className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium">
      <span style={{ background: c.dot }} className="w-1.5 h-1.5 rounded-full" />
      {status?.replace(/_/g,' ')}
    </span>
  );
}

function CatBadge({ category }) {
  const c = CATEGORIES[category] || { label: category, color: '#6b7280' };
  const Icon = c.icon || Car;
  return (
    <span style={{ color: c.color, background: c.color+'18' }}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium">
      <Icon size={11} /> {c.label}
    </span>
  );
}

// ─── Modal shell ───────────────────────────────────────────────────────────────
function Modal({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}>
      <div style={{ background:'#111827', border:'1px solid #1f2937', maxWidth: wide ? 760 : 520 }}
        className="w-full rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-800">
          <h3 className="text-white font-semibold text-lg">{title}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

// ─── Form helpers ──────────────────────────────────────────────────────────────
function Field({ label, children, half }) {
  return (
    <div className={half ? 'col-span-1' : 'col-span-2'}>
      <label className="block text-xs text-gray-400 mb-1">{label}</label>
      {children}
    </div>
  );
}

const inputCls = "w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 focus:outline-none";
const selectCls = inputCls;

// ─── Vehicle Card ──────────────────────────────────────────────────────────────
function VehicleCard({ v, onClick, onCheckout, onEdit, onDelete, canEdit, canDelete }) {
  const Cat = CATEGORIES[v.category] || { icon: Car, color: '#6b7280' };
  const Icon = Cat.icon;
  const hasMaint = v.pending_maintenance > 0;
  
  return (
    <div className="relative group">
      {/* Boutons d'action au survol */}
      <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
        {canEdit && (
          <button 
            onClick={(e) => { e.stopPropagation(); onEdit(v); }}
            className="p-1.5 bg-blue-600 hover:bg-blue-500 rounded-lg text-white transition"
            title="Edit vehicle"
          >
            <Edit2 size={14} />
          </button>
        )}
        {canDelete && (
          <button 
            onClick={(e) => { e.stopPropagation(); onDelete(v); }}
            className="p-1.5 bg-red-600 hover:bg-red-500 rounded-lg text-white transition"
            title="Delete vehicle"
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>
      
      <div onClick={() => onClick(v)}
        style={{ background: '#111827', border: '1px solid #1f2937' }}
        className="rounded-2xl p-5 cursor-pointer hover:border-gray-600 transition-all group">
        <div className="flex items-start justify-between mb-4">
          <div style={{ background: Cat.color+'20', color: Cat.color }}
            className="w-11 h-11 rounded-xl flex items-center justify-center">
            <Icon size={22} />
          </div>
          <div className="flex items-center gap-2">
            {hasMaint && <AlertTriangle size={15} className="text-amber-400" />}
            <StatusBadge status={v.status} />
          </div>
        </div>

        <p className="text-white font-semibold text-base mb-0.5">
          {v.year ? `${v.year} ` : ''}{v.make} {v.model}
        </p>
        <p className="text-gray-500 text-xs mb-3">
          {v.license_plate || '—'} · {v.color || 'No color'}
        </p>

        <div className="flex items-center justify-between">
          <CatBadge category={v.category} />
          <div className="flex items-center gap-1 text-gray-500 text-xs">
            <Gauge size={12} />
            {fmt(v.odometer_km)} km
          </div>
        </div>

        {v.is_available_for_client && v.status === 'available' && (
          <button onClick={e => { e.stopPropagation(); onCheckout(v); }}
            className="mt-3 w-full py-1.5 rounded-lg text-xs font-medium transition-all"
            style={{ background: '#1e40af22', color: '#60a5fa', border: '1px solid #1e40af' }}>
            <Key size={12} className="inline mr-1" /> Checkout to Client
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Vehicle Form ──────────────────────────────────────────────────────────────
function VehicleForm({ initial, onSave, onClose, staffList, truckList }) {
  const [form, setForm] = useState({
    category: 'loaner', make: '', model: '', year: '', color: '',
    license_plate: '', vin: '', fuel_type: 'gasoline', odometer_km: 0,
    status: 'available', notes: '', is_available_for_client: false,
    rental_rate_per_hour: '', rental_rate_per_day: '', fuel_truck_id: '',
    staff_id: '', next_maintenance_km: '', next_maintenance_date: '',
    ...initial,
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="grid grid-cols-2 gap-3">
      <Field label="Category" half>
        <select className={selectCls} value={form.category} onChange={e => set('category', e.target.value)}>
          {Object.entries(CATEGORIES).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
      </Field>
      <Field label="Status" half>
        <select className={selectCls} value={form.status} onChange={e => set('status', e.target.value)}>
          {['available','in_use','maintenance','out_of_service'].map(s => (
            <option key={s} value={s}>{s.replace(/_/g,' ')}</option>
          ))}
        </select>
      </Field>

      {form.category === 'fuel_truck' && (
        <Field label="Link to Fuel Truck">
          <select className={selectCls} value={form.fuel_truck_id} onChange={e => set('fuel_truck_id', e.target.value)}>
            <option value="">— None —</option>
            {truckList.map(t => <option key={t.id} value={t.id}>{t.truck_number} – {t.fuel_type}</option>)}
          </select>
        </Field>
      )}
      {form.category === 'staff_personal' && (
        <Field label="Staff Member">
          <select className={selectCls} value={form.staff_id} onChange={e => set('staff_id', e.target.value)}>
            <option value="">— None —</option>
            {staffList.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
          </select>
        </Field>
      )}

      <Field label="Make" half><input className={inputCls} value={form.make} onChange={e => set('make', e.target.value)} placeholder="Toyota" /></Field>
      <Field label="Model" half><input className={inputCls} value={form.model} onChange={e => set('model', e.target.value)} placeholder="Camry" /></Field>
      <Field label="Year" half><input className={inputCls} type="number" value={form.year} onChange={e => set('year', e.target.value)} placeholder="2023" /></Field>
      <Field label="Color" half><input className={inputCls} value={form.color} onChange={e => set('color', e.target.value)} placeholder="White" /></Field>
      <Field label="License Plate" half><input className={inputCls} value={form.license_plate} onChange={e => set('license_plate', e.target.value)} /></Field>
      <Field label="VIN" half><input className={inputCls} value={form.vin} onChange={e => set('vin', e.target.value)} /></Field>
      <Field label="Fuel Type" half>
        <select className={selectCls} value={form.fuel_type} onChange={e => set('fuel_type', e.target.value)}>
          {['gasoline','diesel','electric','hybrid'].map(f => <option key={f} value={f}>{f}</option>)}
        </select>
      </Field>
      <Field label="Odometer (km)" half><input className={inputCls} type="number" value={form.odometer_km} onChange={e => set('odometer_km', e.target.value)} /></Field>

      <Field label="Next Maintenance (km)" half><input className={inputCls} type="number" value={form.next_maintenance_km} onChange={e => set('next_maintenance_km', e.target.value)} /></Field>
      <Field label="Next Maintenance Date" half><input className={inputCls} type="date" value={form.next_maintenance_date} onChange={e => set('next_maintenance_date', e.target.value)} /></Field>

      <div className="col-span-2 border-t border-gray-800 pt-3">
        <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
          <input type="checkbox" checked={form.is_available_for_client}
            onChange={e => set('is_available_for_client', e.target.checked)}
            className="rounded border-gray-600" />
          Available for client checkout
        </label>
      </div>

      {form.is_available_for_client && (<>
        <Field label="Rate / Hour ($)" half><input className={inputCls} type="number" value={form.rental_rate_per_hour} onChange={e => set('rental_rate_per_hour', e.target.value)} placeholder="Leave empty = free" /></Field>
        <Field label="Rate / Day ($)" half><input className={inputCls} type="number" value={form.rental_rate_per_day} onChange={e => set('rental_rate_per_day', e.target.value)} placeholder="Leave empty = free" /></Field>
      </>)}

      <Field label="Notes"><textarea className={inputCls} rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} /></Field>

      <div className="col-span-2 flex gap-2 pt-2">
        <button onClick={onClose} className="flex-1 py-2 rounded-lg text-sm text-gray-400 border border-gray-700 hover:border-gray-500 transition-colors">Cancel</button>
        <button onClick={() => onSave(form)} className="flex-1 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-500 text-white transition-colors">Save Vehicle</button>
      </div>
    </div>
  );
}

// ─── Checkout Modal ────────────────────────────────────────────────────────────
function CheckoutModal({ vehicle, owners, staff, onSave, onClose }) {
  const [form, setForm] = useState({
    vehicle_id: vehicle.id,
    owner_id: '', staff_id_out: '',
    assignment_type: vehicle.rental_rate_per_day || vehicle.rental_rate_per_hour ? 'rental_paid' : 'loaner_free',
    expected_return_at: '', odometer_out: vehicle.odometer_km || '',
    rate_applied: vehicle.rental_rate_per_day || vehicle.rental_rate_per_hour || '',
    rate_unit: vehicle.rental_rate_per_day ? 'day' : 'hour',
    notes: '',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="col-span-2 p-3 rounded-xl" style={{ background:'#1e3a5f22', border:'1px solid #1e40af44' }}>
        <p className="text-blue-400 text-sm font-medium">{vehicle.year} {vehicle.make} {vehicle.model}</p>
        <p className="text-gray-400 text-xs">{vehicle.license_plate} · {vehicle.color}</p>
      </div>

      <Field label="Client (Owner)" >
        <select className={selectCls} value={form.owner_id} onChange={e => set('owner_id', e.target.value)}>
          <option value="">— Select client —</option>
          {owners.map(o => <option key={o.id} value={o.id}>{o.first_name} {o.last_name} {o.company_name ? `(${o.company_name})` : ''}</option>)}
        </select>
      </Field>

      <Field label="Assignment Type" half>
        <select className={selectCls} value={form.assignment_type} onChange={e => set('assignment_type', e.target.value)}>
          <option value="loaner_free">Free Loaner</option>
          <option value="rental_paid">Paid Rental</option>
        </select>
      </Field>
      <Field label="Staff Out" half>
        <select className={selectCls} value={form.staff_id_out} onChange={e => set('staff_id_out', e.target.value)}>
          <option value="">— Staff —</option>
          {staff.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
        </select>
      </Field>

      <Field label="Odometer Out (km)" half><input className={inputCls} type="number" value={form.odometer_out} onChange={e => set('odometer_out', e.target.value)} /></Field>
      <Field label="Expected Return" half><input className={inputCls} type="datetime-local" value={form.expected_return_at} onChange={e => set('expected_return_at', e.target.value)} /></Field>

      {form.assignment_type === 'rental_paid' && (<>
        <Field label="Rate" half><input className={inputCls} type="number" value={form.rate_applied} onChange={e => set('rate_applied', e.target.value)} /></Field>
        <Field label="Per" half>
          <select className={selectCls} value={form.rate_unit} onChange={e => set('rate_unit', e.target.value)}>
            <option value="hour">Hour</option>
            <option value="day">Day</option>
          </select>
        </Field>
      </>)}

      <Field label="Notes"><textarea className={inputCls} rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} /></Field>

      <div className="col-span-2 flex gap-2 pt-2">
        <button onClick={onClose} className="flex-1 py-2 rounded-lg text-sm text-gray-400 border border-gray-700 hover:border-gray-500">Cancel</button>
        <button onClick={() => onSave(form)} className="flex-1 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-500 text-white">Checkout</button>
      </div>
    </div>
  );
}

// ─── Maintenance Form ──────────────────────────────────────────────────────────
function MaintenanceForm({ vehicleId, onSave, onClose }) {
  const [form, setForm] = useState({
    vehicle_id: vehicleId, maintenance_type: 'scheduled', title: '',
    description: '', technician_name: '', workshop_name: '',
    scheduled_date: '', performed_date: '', odometer_at_service: '',
    labor_cost: '', parts_cost: '', next_due_km: '', next_due_date: '',
    status: 'scheduled', notes: '',
  });
  const [parts, setParts] = useState([]);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const addPart = () => setParts(p => [...p, { part_name:'', part_number:'', quantity:1, unit_cost:0, supplier:'' }]);
  const setPart = (i, k, v) => setParts(p => p.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const removePart = (i) => setParts(p => p.filter((_, idx) => idx !== i));

  const totalParts = parts.reduce((s, p) => s + (p.quantity * p.unit_cost), 0);

  return (
    <div className="grid grid-cols-2 gap-3">
      <Field label="Type" half>
        <select className={selectCls} value={form.maintenance_type} onChange={e => set('maintenance_type', e.target.value)}>
          {MAINT_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g,' ')}</option>)}
        </select>
      </Field>
      <Field label="Status" half>
        <select className={selectCls} value={form.status} onChange={e => set('status', e.target.value)}>
          {['scheduled','in_progress','completed','cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </Field>

      <Field label="Title"><input className={inputCls} value={form.title} onChange={e => set('title', e.target.value)} placeholder="60k oil change" /></Field>

      <Field label="Technician" half><input className={inputCls} value={form.technician_name} onChange={e => set('technician_name', e.target.value)} /></Field>
      <Field label="Workshop" half><input className={inputCls} value={form.workshop_name} onChange={e => set('workshop_name', e.target.value)} /></Field>

      <Field label="Scheduled Date" half><input className={inputCls} type="date" value={form.scheduled_date} onChange={e => set('scheduled_date', e.target.value)} /></Field>
      <Field label="Performed Date" half><input className={inputCls} type="date" value={form.performed_date} onChange={e => set('performed_date', e.target.value)} /></Field>

      <Field label="Odometer at Service (km)" half><input className={inputCls} type="number" value={form.odometer_at_service} onChange={e => set('odometer_at_service', e.target.value)} /></Field>
      <Field label="Labor Cost ($)" half><input className={inputCls} type="number" value={form.labor_cost} onChange={e => set('labor_cost', e.target.value)} /></Field>

      <Field label="Next Due (km)" half><input className={inputCls} type="number" value={form.next_due_km} onChange={e => set('next_due_km', e.target.value)} /></Field>
      <Field label="Next Due Date" half><input className={inputCls} type="date" value={form.next_due_date} onChange={e => set('next_due_date', e.target.value)} /></Field>

      {/* Parts */}
      <div className="col-span-2 border-t border-gray-800 pt-3">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-medium text-gray-300">Parts Used</p>
          <button onClick={addPart} className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1">
            <Plus size={12} /> Add Part
          </button>
        </div>
        {parts.map((p, i) => (
          <div key={i} className="grid grid-cols-5 gap-2 mb-2 items-center">
            <input className={inputCls + ' col-span-2'} placeholder="Part name" value={p.part_name} onChange={e => setPart(i,'part_name',e.target.value)} />
            <input className={inputCls} placeholder="Qty" type="number" value={p.quantity} onChange={e => setPart(i,'quantity',parseFloat(e.target.value))} />
            <input className={inputCls} placeholder="Unit $" type="number" value={p.unit_cost} onChange={e => setPart(i,'unit_cost',parseFloat(e.target.value))} />
            <button onClick={() => removePart(i)} className="text-red-400 hover:text-red-300"><X size={14} /></button>
          </div>
        ))}
        {parts.length > 0 && (
          <p className="text-right text-xs text-gray-400">Parts total: <span className="text-white">{fmtMoney(totalParts)}</span></p>
        )}
      </div>

      <Field label="Notes"><textarea className={inputCls} rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} /></Field>

      <div className="col-span-2 flex gap-2 pt-2">
        <button onClick={onClose} className="flex-1 py-2 rounded-lg text-sm text-gray-400 border border-gray-700 hover:border-gray-500">Cancel</button>
        <button onClick={() => onSave({ ...form, parts })} className="flex-1 py-2 rounded-lg text-sm font-medium bg-amber-600 hover:bg-amber-500 text-white">Save Maintenance</button>
      </div>
    </div>
  );
}

// ─── Vehicle Detail Panel ──────────────────────────────────────────────────────
function VehicleDetail({ vehicle: initialV, onBack, owners, staff, onRefresh }) {
  const [v, setV] = useState(initialV);
  const [tab, setTab] = useState('info');
  const [assignments, setAssignments] = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const [showMaintForm, setShowMaintForm] = useState(false);
  const [showReturn, setShowReturn] = useState(null);
  const [returnForm, setReturnForm] = useState({ odometer_in:'', staff_id_in:'', total_amount:'', notes:'' });

  const load = useCallback(async () => {
    try {
      const [va, vm, vv] = await Promise.all([
        api(`/vehicles/${v.id}/assignments`),
        api(`/vehicles/${v.id}/maintenance`),
        api(`/vehicles/${v.id}`),
      ]);
      setAssignments(va); setMaintenance(vm); setV(vv);
    } catch {}
  }, [v.id]);

  useEffect(() => { load(); }, [load]);

  const saveMaintenance = async (data) => {
    try {
      await api('/vehicles/maintenance', { method: 'POST', body: data });
      setShowMaintForm(false); load();
    } catch (e) { alert('Error: ' + e.message); }
  };

  const doReturn = async () => {
    try {
      await api(`/vehicles/assignments/${showReturn.id}/return`, { method: 'POST', body: returnForm });
      setShowReturn(null); load(); onRefresh();
    } catch (e) { alert('Error: ' + e.message); }
  };

  const Cat = CATEGORIES[v.category] || { icon: Car, color: '#6b7280', label: v.category };
  const Icon = Cat.icon;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={onBack} className="text-gray-400 hover:text-white transition-colors">
          <ArrowLeft size={20} />
        </button>
        <div style={{ background: Cat.color+'20', color: Cat.color }}
          className="w-10 h-10 rounded-xl flex items-center justify-center">
          <Icon size={20} />
        </div>
        <div>
          <h2 className="text-white font-semibold text-lg">
            {v.year} {v.make} {v.model}
          </h2>
          <div className="flex items-center gap-2 mt-0.5">
            <StatusBadge status={v.status} />
            <CatBadge category={v.category} />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 p-1 rounded-xl" style={{ background:'#1f2937' }}>
        {[['info','Info'],['assignments','Assignments'],['maintenance','Maintenance']].map(([k,l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${tab===k ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-gray-200'}`}>
            {l}
          </button>
        ))}
      </div>

      {/* INFO TAB */}
      {tab === 'info' && (
        <div className="grid grid-cols-2 gap-3">
          {[
            ['License Plate', v.license_plate],
            ['VIN', v.vin],
            ['Color', v.color],
            ['Fuel Type', v.fuel_type],
            ['Odometer', `${fmt(v.odometer_km)} km`],
            ['Available for Client', v.is_available_for_client ? 'Yes' : 'No'],
            ['Rate / Hour', v.rental_rate_per_hour ? fmtMoney(v.rental_rate_per_hour) : 'Free'],
            ['Rate / Day', v.rental_rate_per_day ? fmtMoney(v.rental_rate_per_day) : 'Free'],
            ['Next Maint. Date', fmtDate(v.next_maintenance_date)],
            ['Next Maint. km', v.next_maintenance_km ? fmt(v.next_maintenance_km)+' km' : '—'],
            ['Last Maint.', fmtDate(v.last_maintenance_date)],
            ['Staff', v.staff_name || '—'],
          ].map(([label, value]) => (
            <div key={label} className="p-3 rounded-xl" style={{ background:'#1f2937' }}>
              <p className="text-gray-400 text-xs mb-0.5">{label}</p>
              <p className="text-white text-sm font-medium">{value || '—'}</p>
            </div>
          ))}
          {v.notes && (
            <div className="col-span-2 p-3 rounded-xl" style={{ background:'#1f2937' }}>
              <p className="text-gray-400 text-xs mb-1">Notes</p>
              <p className="text-gray-300 text-sm">{v.notes}</p>
            </div>
          )}
        </div>
      )}

      {/* ASSIGNMENTS TAB */}
      {tab === 'assignments' && (
        <div className="space-y-3">
          {assignments.length === 0 && (
            <p className="text-center text-gray-500 py-8">No assignment history</p>
          )}
          {assignments.map(a => (
            <div key={a.id} className="p-4 rounded-xl" style={{ background:'#1f2937', border:'1px solid #374151' }}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <User size={14} className="text-gray-400" />
                  <span className="text-white text-sm font-medium">{a.client_name || 'Unknown client'}</span>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${a.status==='active' ? 'bg-blue-900 text-blue-300' : 'bg-gray-700 text-gray-400'}`}>
                  {a.status}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs text-gray-400">
                <span><Calendar size={10} className="inline mr-1" />{fmtDate(a.checkout_at)}</span>
                <span><RotateCcw size={10} className="inline mr-1" />{a.returned_at ? fmtDate(a.returned_at) : 'Not returned'}</span>
                <span><DollarSign size={10} className="inline mr-1" />{fmtMoney(a.total_amount)}</span>
              </div>
              {a.status === 'active' && (
                <button onClick={() => { setShowReturn(a); setReturnForm({ odometer_in: v.odometer_km || '', staff_id_in:'', total_amount:'', notes:'' }); }}
                  className="mt-2 w-full py-1.5 rounded-lg text-xs text-amber-400 border border-amber-800 hover:border-amber-600 transition-colors">
                  Return Vehicle
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* MAINTENANCE TAB */}
      {tab === 'maintenance' && (
        <div>
          <button onClick={() => setShowMaintForm(true)}
            className="w-full mb-4 py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-all"
            style={{ background:'#451a0322', color:'#fb923c', border:'1px solid #78350f' }}>
            <Plus size={15} /> Add Maintenance Record
          </button>
          {maintenance.length === 0 && (
            <p className="text-center text-gray-500 py-8">No maintenance records</p>
          )}
          {maintenance.map(m => (
            <div key={m.id} className="p-4 rounded-xl mb-3" style={{ background:'#1f2937', border:'1px solid #374151' }}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="text-white text-sm font-medium">{m.title}</p>
                  <p className="text-gray-400 text-xs mt-0.5">{m.maintenance_type?.replace(/_/g,' ')}</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  m.status==='completed' ? 'bg-green-900 text-green-300' :
                  m.status==='in_progress' ? 'bg-amber-900 text-amber-300' :
                  m.status==='scheduled' ? 'bg-blue-900 text-blue-300' :
                  'bg-gray-700 text-gray-400'
                }`}>{m.status}</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs text-gray-400 mb-2">
                {m.technician_name && <span><User size={10} className="inline mr-1" />{m.technician_name}</span>}
                {m.performed_date && <span><Calendar size={10} className="inline mr-1" />{fmtDate(m.performed_date)}</span>}
                {m.next_due_date && <span className="text-amber-400"><Clock size={10} className="inline mr-1" />Next: {fmtDate(m.next_due_date)}</span>}
                <span><DollarSign size={10} className="inline mr-1" />Total: {fmtMoney(m.total_cost)}</span>
              </div>
              {Array.isArray(m.parts) && m.parts.length > 0 && (
                <div className="border-t border-gray-700 pt-2 mt-2">
                  <p className="text-xs text-gray-500 mb-1">Parts ({m.parts.length})</p>
                  {m.parts.map((p, i) => (
                    <div key={i} className="flex justify-between text-xs text-gray-400">
                      <span>{p.part_name} ×{p.quantity}</span>
                      <span>{fmtMoney(p.total_cost)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Return Modal */}
      {showReturn && (
        <Modal title="Return Vehicle" onClose={() => setShowReturn(null)}>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Odometer In (km)" half>
              <input className={inputCls} type="number" value={returnForm.odometer_in}
                onChange={e => setReturnForm(f => ({...f, odometer_in: e.target.value}))} />
            </Field>
            <Field label="Staff In" half>
              <select className={selectCls} value={returnForm.staff_id_in}
                onChange={e => setReturnForm(f => ({...f, staff_id_in: e.target.value}))}>
                <option value="">— Staff —</option>
                {staff.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
              </select>
            </Field>
            <Field label="Total Amount ($)">
              <input className={inputCls} type="number" value={returnForm.total_amount}
                onChange={e => setReturnForm(f => ({...f, total_amount: e.target.value}))} />
            </Field>
            <Field label="Notes">
              <textarea className={inputCls} rows={2} value={returnForm.notes}
                onChange={e => setReturnForm(f => ({...f, notes: e.target.value}))} />
            </Field>
            <div className="col-span-2 flex gap-2 pt-2">
              <button onClick={() => setShowReturn(null)} className="flex-1 py-2 rounded-lg text-sm text-gray-400 border border-gray-700">Cancel</button>
              <button onClick={doReturn} className="flex-1 py-2 rounded-lg text-sm font-medium bg-green-700 hover:bg-green-600 text-white">Confirm Return</button>
            </div>
          </div>
        </Modal>
      )}

      {showMaintForm && (
        <Modal title="New Maintenance Record" onClose={() => setShowMaintForm(false)} wide>
          <MaintenanceForm vehicleId={v.id} onSave={saveMaintenance} onClose={() => setShowMaintForm(false)} />
        </Modal>
      )}
    </div>
  );
}

// ─── Main VehiclesView ─────────────────────────────────────────────────────────
export default function VehiclesView() {
  const { hasMinLevel } = useAuth();
  
  const [vehicles, setVehicles] = useState([]);
  const [activeAssignments, setActiveAssignments] = useState([]);
  const [upcomingMaint, setUpcomingMaint] = useState([]);
  const [owners, setOwners] = useState([]);
  const [staff, setStaff] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showCheckout, setShowCheckout] = useState(null);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [tab, setTab] = useState('fleet');
  
  // États pour l'édition
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editFormData, setEditFormData] = useState({});

  // Vérifier les permissions
  const canEdit = hasMinLevel(90);   // Manager+ peut modifier
  const canDelete = hasMinLevel(100); // Admin uniquement peut supprimer

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [v, aa, um, o, s, ft] = await Promise.all([
        api('/vehicles/'),
        api('/vehicles/assignments/active'),
        api('/vehicles/maintenance/upcoming'),
        api('/owners/'),
        api('/staff/'),
        api('/fuel/trucks/').catch(() => []),
      ]);
      setVehicles(v); setActiveAssignments(aa); setUpcomingMaint(um);
      setOwners(o); setStaff(s); setTrucks(ft);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveVehicle = async (form) => {
    try {
      const clean = Object.fromEntries(Object.entries(form).map(([k,v]) => [k, v==='' ? null : v]));
      await api('/vehicles/', { method: 'POST', body: clean });
      setShowForm(false); load();
    } catch (e) { alert('Error: ' + e.message); }
  };

  const doCheckout = async (form) => {
    try {
      const clean = Object.fromEntries(Object.entries(form).map(([k,v]) => [k, v==='' ? null : v]));
      await api('/vehicles/assignments/checkout', { method: 'POST', body: clean });
      setShowCheckout(null); load();
    } catch (e) { alert('Error: ' + e.message); }
  };

  // Modifier un véhicule
  const handleEditVehicle = (vehicle) => {
    if (!canEdit) {
      alert('You do not have permission to edit vehicles (Manager+ required)');
      return;
    }
    setEditingVehicle(vehicle);
    setEditFormData({
      category: vehicle.category || '',
      make: vehicle.make || '',
      model: vehicle.model || '',
      year: vehicle.year || '',
      color: vehicle.color || '',
      license_plate: vehicle.license_plate || '',
      vin: vehicle.vin || '',
      fuel_type: vehicle.fuel_type || 'gasoline',
      odometer_km: vehicle.odometer_km || 0,
      status: vehicle.status || 'available',
      is_available_for_client: vehicle.is_available_for_client || false,
      rental_rate_per_hour: vehicle.rental_rate_per_hour || '',
      rental_rate_per_day: vehicle.rental_rate_per_day || '',
      next_maintenance_km: vehicle.next_maintenance_km || '',
      next_maintenance_date: vehicle.next_maintenance_date || '',
      notes: vehicle.notes || '',
      fuel_truck_id: vehicle.fuel_truck_id || '',
      staff_id: vehicle.staff_id || '',
    });
    setShowEditForm(true);
  };

  // Sauvegarder la modification
  const saveVehicleEdit = async () => {
    try {
      const clean = Object.fromEntries(
        Object.entries(editFormData).map(([k, v]) => [k, v === '' ? null : v])
      );
      await api(`/vehicles/${editingVehicle.id}`, { method: 'PUT', body: clean });
      setShowEditForm(false);
      setEditingVehicle(null);
      load();
    } catch (e) {
      alert('Error updating vehicle: ' + e.message);
    }
  };

  // Supprimer un véhicule
  const handleDeleteVehicle = async (vehicle) => {
    if (!canDelete) {
      alert('You do not have permission to delete vehicles (Admin required)');
      return;
    }
    
    if (window.confirm(`Delete vehicle "${vehicle.make} ${vehicle.model}" (${vehicle.license_plate})?\n\nThis will deactivate the vehicle.`)) {
      try {
        await api(`/vehicles/${vehicle.id}`, { method: 'DELETE' });
        load();
      } catch (e) {
        alert('Error deleting vehicle: ' + e.message);
      }
    }
  };

  const filtered = vehicles.filter(v => {
    const q = search.toLowerCase();
    const match = !q || [v.make, v.model, v.license_plate, v.color].some(f => f?.toLowerCase().includes(q));
    const cat = catFilter === 'all' || v.category === catFilter;
    return match && cat;
  });

  // Stats
  const stats = {
    total: vehicles.length,
    available: vehicles.filter(v => v.status === 'available').length,
    inUse: vehicles.filter(v => v.status === 'in_use').length,
    maintenance: vehicles.filter(v => v.status === 'maintenance').length,
  };

  if (selected) {
    return (
      <VehicleDetail
        vehicle={selected} owners={owners} staff={staff}
        onBack={() => { setSelected(null); load(); }}
        onRefresh={load}
      />
    );
  }

  return (
    <div>
      {/* Stats bar */}
      <div className="grid grid-cols-4 gap-3 mb-6">
        {[
          { label:'Total Fleet', value: stats.total, color:'#6b7280', Icon: Car },
          { label:'Available', value: stats.available, color:'#22c55e', Icon: CheckCircle },
          { label:'In Use', value: stats.inUse, color:'#3b82f6', Icon: Key },
          { label:'In Maintenance', value: stats.maintenance, color:'#f97316', Icon: Wrench },
        ].map(({ label, value, color, Icon: I }) => (
          <div key={label} className="p-4 rounded-2xl" style={{ background:'#111827', border:'1px solid #1f2937' }}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-gray-400 text-xs">{label}</p>
              <I size={16} style={{ color }} />
            </div>
            <p className="text-white text-2xl font-bold">{value}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-1 p-1 rounded-xl" style={{ background:'#1f2937' }}>
          {[['fleet','Fleet'],['assignments','Active Checkouts'],['maintenance','Maintenance']].map(([k,l]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab===k ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-gray-200'}`}>
              {l}
              {k === 'assignments' && activeAssignments.length > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-xs bg-blue-600 text-white">{activeAssignments.length}</span>
              )}
              {k === 'maintenance' && upcomingMaint.length > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-xs bg-amber-600 text-white">{upcomingMaint.length}</span>
              )}
            </button>
          ))}
        </div>
        <button onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium bg-blue-600 hover:bg-blue-500 text-white transition-colors">
          <Plus size={16} /> Add Vehicle
        </button>
      </div>

      {/* FLEET TAB */}
      {tab === 'fleet' && (
        <>
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input className={inputCls + ' pl-9'} placeholder="Search make, model, plate..."
                value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <select className={selectCls + ' w-40'} value={catFilter} onChange={e => setCatFilter(e.target.value)}>
              <option value="all">All categories</option>
              {Object.entries(CATEGORIES).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          {loading ? (
            <div className="text-center py-16 text-gray-500">Loading fleet...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-gray-500">No vehicles found</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filtered.map(v => (
                <VehicleCard 
                  key={v.id} 
                  v={v} 
                  onClick={setSelected} 
                  onCheckout={setShowCheckout}
                  onEdit={handleEditVehicle}
                  onDelete={handleDeleteVehicle}
                  canEdit={canEdit}
                  canDelete={canDelete}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* ASSIGNMENTS TAB */}
      {tab === 'assignments' && (
        <div className="space-y-3">
          {activeAssignments.length === 0 && (
            <div className="text-center py-16 text-gray-500">No active checkouts</div>
          )}
          {activeAssignments.map(a => (
            <div key={a.id} className="p-4 rounded-2xl flex items-center gap-4"
              style={{ background:'#111827', border:'1px solid #1f2937' }}>
              <div style={{ background:'#1e3a5f', color:'#60a5fa' }}
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0">
                <Car size={18} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-white text-sm font-medium">{a.year} {a.make} {a.model}</p>
                  <span className="text-xs text-gray-500">{a.license_plate}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-amber-900 text-amber-300">
                    {a.assignment_type === 'rental_paid' ? 'Rental' : 'Loaner'}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-400">
                  <span><User size={10} className="inline mr-1" />{a.client_name || '—'}</span>
                  <span><Calendar size={10} className="inline mr-1" />Out: {fmtDate(a.checkout_at)}</span>
                  {a.expected_return_at && <span className="text-amber-400"><Clock size={10} className="inline mr-1" />Expected: {fmtDate(a.expected_return_at)}</span>}
                </div>
              </div>
              <button onClick={() => setSelected({ id: a.vehicle_id, make: a.make, model: a.model, category: a.category })}
                className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1">
                View <ChevronRight size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* MAINTENANCE TAB */}
      {tab === 'maintenance' && (
        <div className="space-y-3">
          {upcomingMaint.length === 0 && (
            <div className="text-center py-16 text-gray-500">No upcoming maintenance</div>
          )}
          {upcomingMaint.map(m => (
            <div key={m.id} className="p-4 rounded-2xl flex items-center gap-4"
              style={{ background:'#111827', border:'1px solid #1f2937' }}>
              <div style={{ background:'#451a0322', color:'#fb923c' }}
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0">
                <Wrench size={18} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-white text-sm font-medium">{m.title}</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    m.status==='in_progress' ? 'bg-amber-900 text-amber-300' : 'bg-blue-900 text-blue-300'
                  }`}>{m.status}</span>
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-400">
                  <span><Car size={10} className="inline mr-1" />{m.make} {m.model} · {m.license_plate}</span>
                  {m.next_due_date && <span className="text-amber-400"><Calendar size={10} className="inline mr-1" />{fmtDate(m.next_due_date)}</span>}
                  {m.next_due_km && <span><Gauge size={10} className="inline mr-1" />{fmt(m.next_due_km)} km</span>}
                </div>
              </div>
              <CatBadge category={m.category} />
            </div>
          ))}
        </div>
      )}

      {/* Modal d'édition de véhicule */}
      {showEditForm && editingVehicle && (
        <Modal title={`Edit Vehicle: ${editingVehicle.make} ${editingVehicle.model}`} onClose={() => setShowEditForm(false)} wide>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category" half>
              <select className={selectCls} value={editFormData.category} onChange={e => setEditFormData({...editFormData, category: e.target.value})}>
                {Object.entries(CATEGORIES).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Status" half>
              <select className={selectCls} value={editFormData.status} onChange={e => setEditFormData({...editFormData, status: e.target.value})}>
                {['available','in_use','maintenance','out_of_service'].map(s => (
                  <option key={s} value={s}>{s.replace(/_/g,' ')}</option>
                ))}
              </select>
            </Field>

            {editFormData.category === 'fuel_truck' && (
              <Field label="Link to Fuel Truck">
                <select className={selectCls} value={editFormData.fuel_truck_id} onChange={e => setEditFormData({...editFormData, fuel_truck_id: e.target.value})}>
                  <option value="">— None —</option>
                  {trucks.map(t => <option key={t.id} value={t.id}>{t.truck_number} – {t.fuel_type}</option>)}
                </select>
              </Field>
            )}
            {editFormData.category === 'staff_personal' && (
              <Field label="Staff Member">
                <select className={selectCls} value={editFormData.staff_id} onChange={e => setEditFormData({...editFormData, staff_id: e.target.value})}>
                  <option value="">— None —</option>
                  {staff.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
                </select>
              </Field>
            )}

            <Field label="Make" half><input className={inputCls} value={editFormData.make} onChange={e => setEditFormData({...editFormData, make: e.target.value})} placeholder="Toyota" /></Field>
            <Field label="Model" half><input className={inputCls} value={editFormData.model} onChange={e => setEditFormData({...editFormData, model: e.target.value})} placeholder="Camry" /></Field>
            <Field label="Year" half><input className={inputCls} type="number" value={editFormData.year} onChange={e => setEditFormData({...editFormData, year: e.target.value})} placeholder="2023" /></Field>
            <Field label="Color" half><input className={inputCls} value={editFormData.color} onChange={e => setEditFormData({...editFormData, color: e.target.value})} placeholder="White" /></Field>
            <Field label="License Plate" half><input className={inputCls} value={editFormData.license_plate} onChange={e => setEditFormData({...editFormData, license_plate: e.target.value})} /></Field>
            <Field label="VIN" half><input className={inputCls} value={editFormData.vin} onChange={e => setEditFormData({...editFormData, vin: e.target.value})} /></Field>
            <Field label="Fuel Type" half>
              <select className={selectCls} value={editFormData.fuel_type} onChange={e => setEditFormData({...editFormData, fuel_type: e.target.value})}>
                {['gasoline','diesel','electric','hybrid'].map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </Field>
            <Field label="Odometer (km)" half><input className={inputCls} type="number" value={editFormData.odometer_km} onChange={e => setEditFormData({...editFormData, odometer_km: e.target.value})} /></Field>

            <Field label="Next Maintenance (km)" half><input className={inputCls} type="number" value={editFormData.next_maintenance_km} onChange={e => setEditFormData({...editFormData, next_maintenance_km: e.target.value})} /></Field>
            <Field label="Next Maintenance Date" half><input className={inputCls} type="date" value={editFormData.next_maintenance_date} onChange={e => setEditFormData({...editFormData, next_maintenance_date: e.target.value})} /></Field>

            <div className="col-span-2 border-t border-gray-800 pt-3">
              <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                <input type="checkbox" checked={editFormData.is_available_for_client}
                  onChange={e => setEditFormData({...editFormData, is_available_for_client: e.target.checked})}
                  className="rounded border-gray-600" />
                Available for client checkout
              </label>
            </div>

            {editFormData.is_available_for_client && (<>
              <Field label="Rate / Hour ($)" half><input className={inputCls} type="number" value={editFormData.rental_rate_per_hour} onChange={e => setEditFormData({...editFormData, rental_rate_per_hour: e.target.value})} placeholder="Leave empty = free" /></Field>
              <Field label="Rate / Day ($)" half><input className={inputCls} type="number" value={editFormData.rental_rate_per_day} onChange={e => setEditFormData({...editFormData, rental_rate_per_day: e.target.value})} placeholder="Leave empty = free" /></Field>
            </>)}

            <Field label="Notes"><textarea className={inputCls} rows={2} value={editFormData.notes} onChange={e => setEditFormData({...editFormData, notes: e.target.value})} /></Field>

            <div className="col-span-2 flex gap-2 pt-2">
              <button onClick={() => setShowEditForm(false)} className="flex-1 py-2 rounded-lg text-sm text-gray-400 border border-gray-700 hover:border-gray-500">Cancel</button>
              <button onClick={saveVehicleEdit} className="flex-1 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-500 text-white">Save Changes</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modals */}
      {showForm && (
        <Modal title="Add Vehicle" onClose={() => setShowForm(false)} wide>
          <VehicleForm onSave={saveVehicle} onClose={() => setShowForm(false)} staffList={staff} truckList={trucks} />
        </Modal>
      )}
      {showCheckout && (
        <Modal title="Checkout Vehicle" onClose={() => setShowCheckout(null)}>
          <CheckoutModal vehicle={showCheckout} owners={owners} staff={staff}
            onSave={doCheckout} onClose={() => setShowCheckout(null)} />
        </Modal>
      )}
    </div>
  );
}